import cn from 'classnames';
import React from 'react';

import { useTypedSelector } from '../../../../hooks';
import { getAppliedDiscount, getQuote } from '../../../../modules/GeneralFlow/ducks';
import { getIsExcludingVat } from '../../../../modules/User/ducks';
import { basisPointsAsString, priceAsString } from '../../../../utils/price';
import { IconSmile } from '../../basics/icons';
import './quote-price-display.scss';

interface Props {
  addOriginalPrice: boolean;
}

export const QuotePriceDisplay = ({ addOriginalPrice }: Props): JSX.Element | null => {
  const quote = useTypedSelector(getQuote);
  const appliedDiscount = useTypedSelector(getAppliedDiscount);
  const isExclVat = useTypedSelector(getIsExcludingVat);
  const isLoading = useTypedSelector(state => state.generalTransport.price.isLoading);
  const isBlocked = !!quote?.blocked?.result;
  // A blocked quote shows no amount at all, so there is nothing to discount
  const discount = isBlocked ? null : appliedDiscount;
  const vatKey = isExclVat ? 'excl_vat' : 'incl_vat';
  const price = quote?.price?.[vatKey];
  const originalPrice = addOriginalPrice ? discount?.original_price?.[vatKey] : undefined;

  const isDiscountShown = originalPrice && discount;
  return (
    <div
      data-qa-id="price-display"
      data-qa-id-price-loading={isLoading ? 'true' : 'false'}
      className={'inline-block whitespace-nowrap font-bariol'}
    >
      {isDiscountShown && (
        <div className={'text-xs relative'} style={{ lineHeight: '.75' }}>
          <span className={'mr-2 text-grey-600 line-through'}>
            {priceAsString({ price: originalPrice, addPlusMinusIndicator: false })}
          </span>
          <span
            className={'text-[.75em] px-[0.6em] py-[0.2em] text-green-500 bg-green-200 rounded relative top-[-0.125em]'}
          >
            {basisPointsAsString(-Math.abs(discount.percentage_basis_points))}%
          </span>
        </div>
      )}
      <span className={cn('relative inline-block text-3xl')}>
        <span
          className={cn({
            'quote-price-display--is-visible': !isLoading,
            'quote-price-display--is-discounted text-green-500': !!discount && !isLoading,
            'text-grey-600': isLoading,
            'text-primary': !discount && !isLoading,
          })}
        >
          {isBlocked ? '--' : priceAsString({ price, addPlusMinusIndicator: false })}
        </span>
        {isLoading && (
          <span
            className={'absolute top-1/2 -translate-y-1/2 left-full ml-6 md:left-auto md:right-full md:ml-0 md:mr-2'}
            style={{ fontSize: '0.6em' }}
          >
            <IconSmile spinning={true} />
          </span>
        )}
      </span>
    </div>
  );
};
